import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { companiesQueryOptions, companyBySlugQueryOptions } from '@/entities/company'
import { useSession } from '@/entities/session'
import { EntityTagsEditor } from '@/entities/tag'
import { ArchiveCompanyDialog, useArchiveCompany } from '@/features/archive-company'
import { CompanyLinksBlock, useEditCompany } from '@/features/edit-company'
import { useSearchStore } from '@/features/search'
import { useToggleCompanyVisibility } from '@/features/toggle-visibility'
import { ApiError, writeThroughErrorText } from '@/shared/api'
import { flattenPages, LIST_SLICES, type ListSlice } from '@/shared/lib'
import { Breadcrumb } from '@/shared/ui/breadcrumb'
import { Button } from '@/shared/ui/button'
import { Icon } from '@/shared/ui/icon'
import { InlineText } from '@/shared/ui/inline-edit'
import { Menu } from '@/shared/ui/menu'
import { MoneyAmount } from '@/shared/ui/money-amount'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'
import { toast } from '@/shared/ui/toast'
import { ActivityFeed } from '@/widgets/activity-feed'
import { CommentThread } from '@/widgets/comment-thread'
import { CompanyPeople } from '@/widgets/company-people'
import { DocumentSlots } from '@/widgets/document-slots'
import { EntityPageHeader, EntityPageLayout, type EntityPageTab, Meta } from '@/widgets/entity-page'
import { InvoiceList } from '@/widgets/invoice-list'
import { MeetingList } from '@/widgets/meeting-list'
import { CompanyContacts } from './-ui/company-contacts'
import { CompanyProjects } from './-ui/company-projects'
import { RequisitesGrid } from './-ui/company-requisites'
import {
  CUSTOMER_HUB_TABS,
  CustomerCompanyHub,
  type CustomerHubTab,
} from './-ui/customer-company-hub'

const COMPANY_TABS = [
  'projects',
  'people',
  'requisites',
  'contacts',
  'links',
  'docs',
  'invoices',
  'meetings',
  'comments',
] as const
type CompanyTab = (typeof COMPANY_TABS)[number]

// Один роут — два контура: у сотрудника свой набор табов, у заказчика (хаб) —
// свой; search-схема принимает объединение, невалидное → undefined.
const ALL_TABS = [...new Set<string>([...COMPANY_TABS, ...CUSTOMER_HUB_TABS])] as [
  string,
  ...string[],
]

// slice — срез вкладки «Проекты» (активные / архив / скрытые / все); дефолт
// (active) в адрес не пишем.
const CompanySearchSchema = z.object({
  tab: z.enum(ALL_TABS).optional().catch(undefined),
  slice: z.enum(LIST_SLICES).optional().catch(undefined),
})

// Страница компании: сотруднику — карточка компании, заказчику — его хаб
// (шапка со сводкой сделок + табы заказчика). Чужая компания заказчику
// недоступна — бэк отвечает 403 (existence-hiding).
export const Route = createFileRoute('/_app/companies/$company')({
  validateSearch: (search) => CompanySearchSchema.parse(search),
  head: () => ({ meta: [{ title: 'Компания · STIT Deal' }] }),
  component: CompanyPage,
})

const TAB_LABELS: Record<CompanyTab, string> = {
  projects: 'Проекты',
  people: 'Люди',
  requisites: 'Реквизиты',
  contacts: 'Контакты',
  links: 'Ссылки',
  docs: 'Документы',
  invoices: 'Счета',
  meetings: 'Встречи',
  comments: 'Комментарии',
}

function CompanyPage() {
  const { session } = useSession()
  if (session?.kind === 'customer') return <CustomerCompanyPage />
  return <EmployeeCompanyPage />
}

// Хаб заказчика: та же страница, customer-вариант (шапка-сводка + свои табы).
function CustomerCompanyPage() {
  const { company } = Route.useParams()
  const { tab } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { session } = useSession()

  const companyQ = useQuery(companyBySlugQueryOptions(company))

  if (companyQ.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }
  if (companyQ.error) {
    const e = companyQ.error
    if (e instanceof ApiError && (e.isForbidden || e.status === 404)) {
      return <StatusScreen title="Компания недоступна" message="Обратитесь к менеджеру." />
    }
    return <StatusScreen title="Не удалось загрузить компанию" message="Обновите страницу." />
  }

  const hubTab = CUSTOMER_HUB_TABS.find((t) => t === tab)
  return (
    <CustomerCompanyHub
      company={companyQ.data}
      isAdmin={session?.isAdmin ?? false}
      tab={hubTab}
      onTabChange={(value: CustomerHubTab) => void navigate({ search: { tab: value } })}
    />
  )
}

function EmployeeCompanyPage() {
  const { company } = Route.useParams()
  const { tab, slice } = Route.useSearch()
  const navigate = Route.useNavigate()
  const projectsSlice: ListSlice = slice ?? 'active'
  const archive = useArchiveCompany()
  const [archiveOpen, setArchiveOpen] = useState(false)

  const companyQ = useQuery(companyBySlugQueryOptions(company))
  // Inline-правка имени. Slug — read-only проекция хаба (правка не пробрасывается).
  const edit = useEditCompany(companyQ.data?.id ?? '')
  const toggleVisibility = useToggleCompanyVisibility(companyQ.data?.id ?? '')
  // CompanyView не несёт hidden — берём видимый набор и выводим hidden разницей.
  // Архивные в наборе нужны: карточка архивной компании открывается по ссылке.
  const visibleQ = useInfiniteQuery(companiesQueryOptions({ includeArchived: true }))

  if (companyQ.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }
  if (companyQ.error) {
    const e = companyQ.error
    if (e instanceof ApiError && e.status === 404) {
      return <StatusScreen title="Компания не найдена" message="Проверьте адрес." />
    }
    return <StatusScreen title="Не удалось загрузить компанию" message="Обновите страницу." />
  }

  const c = companyQ.data
  const activeTab: CompanyTab = COMPANY_TABS.find((t) => t === tab) ?? 'projects'
  const hidden = !flattenPages(visibleQ.data?.pages).some((x) => x.id === c.id)

  const tabs: EntityPageTab<CompanyTab>[] = COMPANY_TABS.map((value) => ({
    value,
    label: TAB_LABELS[value],
    content:
      value === 'projects' ? (
        <CompanyProjects
          companyId={c.id}
          slice={projectsSlice}
          onSliceChange={(next) =>
            void navigate({
              search: { tab: 'projects', ...(next === 'active' ? {} : { slice: next }) },
            })
          }
        />
      ) : value === 'people' ? (
        <CompanyPeople companyId={c.id} canManage />
      ) : value === 'requisites' ? (
        <RequisitesGrid company={c} />
      ) : value === 'contacts' ? (
        <CompanyContacts company={c} />
      ) : value === 'links' ? (
        <CompanyLinksBlock company={c} editable />
      ) : value === 'docs' ? (
        <DocumentSlots
          entityKind="company"
          entityId={c.id}
          withProjects
          docLink={(docId) => ({
            to: '/companies/$company/documents/$doc',
            params: { company: c.slug, doc: docId },
          })}
          projectDocLink={(docId, project) => ({
            to: '/$company/$project/documents/$doc',
            params: { company: c.slug, project: project.slug, doc: docId },
          })}
          projectLink={(project) => ({
            to: '/$company/$project',
            params: { company: c.slug, project: project.slug },
          })}
        />
      ) : value === 'invoices' ? (
        <InvoiceList
          source={{ kind: 'company', companyId: c.id }}
          isEmployee
          showProjectColumn
          invoiceLink={(invoiceId) => ({
            to: '/companies/$company/invoices/$invoice',
            params: { company: c.slug, invoice: invoiceId },
          })}
        />
      ) : value === 'meetings' ? (
        <MeetingList
          scope={{ kind: 'company', id: c.id }}
          isEmployee
          showProject
          meetingLink={(meetingId) => ({
            to: '/companies/$company/meetings/$meeting',
            params: { company: c.slug, meeting: meetingId },
          })}
        />
      ) : (
        <CommentThread entityKind="company" entityId={c.id} />
      ),
  }))

  const header = (
    <EntityPageHeader
      breadcrumb={
        <Breadcrumb items={[{ label: 'Компании', to: '/companies' }, { label: c.name }]} />
      }
      titleText={c.name}
      title={
        <InlineText
          value={c.name}
          ariaLabel="Название компании"
          validate={(v) => (v.trim() ? null : 'Укажите название')}
          onSave={(v) =>
            edit.mutate(
              { name: v },
              { onError: (e) => toast.error(writeThroughErrorText(e) ?? 'Не удалось сохранить') },
            )
          }
        />
      }
      subtitle={
        // Slug — проекция хаба, read-only (правка адреса живёт в хабе).
        <span className="flex items-center gap-2 font-mono text-text-tertiary text-xs">
          <span title="Адрес компании (задаётся в хабе)">{c.slug}</span>
          {hidden && <span className="label text-text-tertiary">скрыта в deal</span>}
          {c.archived && <span className="label text-text-tertiary">в архиве</span>}
        </span>
      }
      meta={
        <>
          {c.project_count != null && (
            <Meta label="Проектов">
              <span className="mono-num text-sm text-text-primary">{c.project_count}</span>
            </Meta>
          )}
          {c.projects_total_kopecks != null && (
            <Meta label="Сумма по проектам">
              <MoneyAmount kopecks={c.projects_total_kopecks} className="text-base" />
            </Meta>
          )}
        </>
      }
      tags={
        <EntityTagsEditor
          entityKind="company"
          entityId={c.id}
          tags={c.tags ?? []}
          onTagClick={(tag) => useSearchStore.getState().openWithTag(tag)}
        />
      }
      actions={
        <>
          <Menu
            align="end"
            aria-label="Действия с компанией"
            trigger={<Icon name="dots" size={18} />}
            triggerClassName="p-2"
            items={[
              {
                label: hidden ? 'Показать в deal' : 'Скрыть в deal',
                icon: 'eye',
                onSelect: () => toggleVisibility.mutate(!hidden),
              },
            ]}
          />
          {c.archived ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => archive.mutate({ id: c.id, archived: false })}
            >
              <Icon name="check" size={16} />
              Вернуть из архива
            </Button>
          ) : (
            <Button variant="secondary" size="sm" onClick={() => setArchiveOpen(true)}>
              <Icon name="collapse" size={16} />В архив
            </Button>
          )}
        </>
      }
    />
  )

  return (
    <>
      <EntityPageLayout
        header={header}
        tabs={tabs}
        activeTab={activeTab}
        // Срез вкладки «Проекты» переживает переключение вкладок.
        onTabChange={(value) =>
          void navigate({ search: { tab: value, ...(slice ? { slice } : {}) } })
        }
        tabsLabel="Разделы компании"
        rail={<ActivityFeed source={{ kind: 'company', id: c.id }} />}
        railTitle="Лента"
      />

      {archiveOpen && (
        <ArchiveCompanyDialog
          company={{
            id: c.id,
            name: c.name,
            ...(c.project_count != null ? { project_count: c.project_count } : {}),
          }}
          open
          onClose={() => setArchiveOpen(false)}
          onArchived={() => void navigate({ to: '/companies' })}
        />
      )}
    </>
  )
}
